import cx from 'classnames';
import React, { memo, useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import ContextMenu from '../../../../../../components/other/context-menu';
import {
  applyTemplateService,
  deleteTemplateService,
  getTemplatesService,
  setTemplateAutoService,
} from '../../../../../../services/cna/assignments/cna';
import { isResponseOk } from '../../../../../../utility/isResponseOk';

import './styles.scss';

const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="8.5" stroke="#7F56D9" strokeWidth="1.5" />
    <path d="M10 9V14" stroke="#7F56D9" strokeWidth="1.67" strokeLinecap="round" />
    <circle cx="10" cy="6.25" r="1" fill="#7F56D9" />
  </svg>
);

const ShiftTemplatesListModal = ({
  shiftID,
  shiftTimeType,
  unitName,
  onClose,
  setAddNewTemplateModalData,
  onRefetchAssignment,
  hasTemplateWritePermission,
}) => {
  const [templatesList, setTemplatesList] = useState([]);
  const [togglingAutoID, setTogglingAutoID] = useState(null);
  const [applyingID, setApplyingID] = useState(null);

  const isBusy = !!applyingID || !!togglingAutoID;

  const shiftLabel = shiftTimeType ? shiftTimeType.replace(/^SHIFT/i, 'Shift') : '';
  const hasAutoTemplate = templatesList?.some((template) => template.is_auto);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await getTemplatesService({ shiftID, shiftTypeOnly: true });
      if (isResponseOk(res) && res.data) {
        setTemplatesList(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [shiftID]);

  useEffect(() => {
    fetchTemplates();
  }, [shiftID]);

  const handleApplyTemplate = useCallback(
    async (templateID) => {
      if (isBusy) return;
      try {
        setApplyingID(templateID);
        const res = await applyTemplateService({ shiftID, templateID });
        if (isResponseOk(res)) {
          await onRefetchAssignment();
          onClose();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setApplyingID(null);
      }
    },
    [shiftID, isBusy, onRefetchAssignment, onClose],
  );

  const handleToggleAuto = useCallback(
    async (templateID, isAuto) => {
      if (isBusy) return;
      try {
        setTogglingAutoID(templateID);
        const res = await setTemplateAutoService({ shiftID, templateID, isAuto });
        if (isResponseOk(res)) {
          await fetchTemplates();
          await onRefetchAssignment();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTogglingAutoID(null);
      }
    },
    [shiftID, isBusy, fetchTemplates, onRefetchAssignment],
  );

  const handleDeleteTemplateService = useCallback(
    async (templateID) => {
      try {
        const res = await deleteTemplateService({ shiftID, templateID });
        if (isResponseOk(res)) {
          await fetchTemplates();
          // Keeps the shift's has_templates flag (and so the Templates button colour) in sync.
          await onRefetchAssignment();
        }
      } catch (e) {
        console.error(e);
      }
    },
    [shiftID, fetchTemplates, onRefetchAssignment],
  );

  const renderTemplateActionsMenu = (shiftID, template) => {
    const menu = [
      {
        label: 'Edit',
        icon: <i className="icon-icon-edit" />,
        action: ({ onCloseMenu }) => {
          onCloseMenu();
          setAddNewTemplateModalData({ shiftID, templateData: template });
          onClose();
        },
      },
      {
        label: 'Delete',
        color: '#B32318',
        icon: <i className="icon-remove" />,
        action: ({ onCloseMenu }) => {
          onCloseMenu();
          handleDeleteTemplateService(template.id);
        },
      },
    ];

    return <ContextMenu size={'small'} menu={menu} styles={{ background: 'transparent', marginTop: 4 }} btnClassName="action-button" />;
  };

  return (
    <Modal
      isOpen
      className={cx('modal-default shift-templates-list-modal')}
      overlayClassName={'modal-overlay'}
      onRequestClose={onClose}
      contentLabel="Shift templates list"
      ariaHideApp={false}
    >
      <h2 className="modal-header">
        {`Templates list${shiftLabel ? ` - ${shiftLabel}` : ''}`}
        <button className="btn btn-icon" onClick={onClose}>
          <i className="icon-icon-x" />
        </button>
      </h2>
      <div className="modal-body">
        <span className={'group__title'}>Clicking a template will overwrite all existing table data</span>

        <div className="templates-group">
          <div className="templates-group__header">
            <span className="templates-group__header__title">Templates</span>
          </div>
          <ul className="templates-group__list">
            {templatesList?.map((template) => (
              <li className="templates-group__item" key={template.id}>
                <div className="templates-group__item__title">
                  <span>{template.name}</span>
                </div>
                <div className="templates-group__item__actions">
                  {(applyingID === template.id || togglingAutoID === template.id) && <span className="row-spinner" />}
                  <label className={cx('auto-checkbox', { disabled: !hasTemplateWritePermission || isBusy })}>
                    <input
                      type="checkbox"
                      checked={!!template.is_auto}
                      disabled={!hasTemplateWritePermission || isBusy}
                      onChange={() => handleToggleAuto(template.id, !template.is_auto)}
                    />
                    <span>Auto</span>
                  </label>
                  <button
                    className="btn apply-button"
                    disabled={isBusy}
                    onClick={() => {
                      handleApplyTemplate(template.id);
                    }}
                  >
                    Apply
                  </button>
                  {hasTemplateWritePermission && renderTemplateActionsMenu(shiftID, template)}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {hasAutoTemplate && (
          <div className="templates-auto-info">
            <InfoIcon />
            <span>
              {`This template will automatically be applied to every future ${shiftLabel || 'shift'}${
                unitName ? ` for the ${unitName} unit` : ''
              }.`}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default memo(ShiftTemplatesListModal);
